import React from 'react'
import { Link } from 'react-router-dom'

import { useMutation, useSubscription } from '@apollo/client'
import {
  Avatar,
  Button,
  Divider,
  Slide,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import Img from 'react-graceful-image'

import { UserContext } from '../../App'
import {
  CREATE_COMMENT,
  LIKE_POST,
  SAVE_POST,
  UNLIKE_POST,
  UNSAVE_POST
} from '../../graphql/mutations'
import { GET_POST } from '../../graphql/subscriptions'
import {
  CommentIcon,
  LikeIcon,
  MoreIcon,
  RemoveIcon,
  SaveIcon,
  ShareIcon,
  UnlikeIcon
} from '../../icons'
import { usePostStyles } from '../../styles'
import { formatDateToNowShort, formatPostDate } from '../../utils/formatDate'
import OptionsDialog from '../shared/OptionsDialog'
import UserCard from '../shared/UserCard'
import PostSkeleton from './PostSkeleton'

function Post({ postId, modalOpen = false }) {
  const classes = usePostStyles()
  const variables = {
    postId
  }
  const { data, loading } = useSubscription(GET_POST, { variables })
  const [showOptionsDialog, setOptionsDialog] = React.useState(false)
  const [postCommentVisible, setPostCommentVisible] = React.useState(false)
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))
  const xsOnly = useMediaQuery(theme.breakpoints.only('xs'))
  const [open, setOpen] = React.useState(false)

  if (loading) return <PostSkeleton />

  const {
    id,
    media,
    likes,
    likes_aggregate,
    saved_posts,
    location,
    user,
    caption,
    comments,
    created_at
  } = data.posts_by_pk
  const likesCount = likes_aggregate.aggregate.count

  return (
    <div className={classes.postContainer}>
      <article className={classes.article}>
        {/* Post Header */}
        <div className={classes.postHeader}>
          <UserCard user={user} location={location} avatarSize={32} />
          <MoreIcon onClick={() => setOptionsDialog(true)} />
        </div>
        {/* Post Image */}
        <div className={classes.postImage}>
          <Img src={media} alt="Post media" className={classes.image} />
        </div>
        {/* Post Button */}
        <div className={classes.postButtonsWrapper}>
          <div className={classes.postButtons}>
            <LikeButton likes={likes} postId={id} authorId={user.id} />
            <CommentIcon onClick={() => setPostCommentVisible(true)} />
            <ShareIcon />
            <SaveButton savedPosts={saved_posts} postId={id} />
          </div>
          <Typography className={classes.likes} variant="subtitle2">
            <span>{likesCount === 1 ? '1 like' : `${likesCount} likes`}</span>
          </Typography>
          <div className={classes.postCommentContainer}>
            <AuthorCaption
              user={user}
              createdAt={created_at}
              caption={caption}
            />
            {comments.map(comment => (
              <UserComment key={comment.id} comment={comment} />
            ))}
          </div>
          <Typography color="textSecondary" className={classes.datePosted}>
            {formatPostDate(created_at)}
          </Typography>
          {((smDown && postCommentVisible) || !xsOnly) && (
            <div className={classes.comment}>
              <Divider />
              <Comment postId={id} />
            </div>
          )}
        </div>
      </article>
      {showOptionsDialog && (
        <OptionsDialog
          postId={id}
          authorId={user.id}
          onClose={({ linkCopied }) => {
            setOpen(linkCopied)
            setOptionsDialog(false)
          }}
          modalOpen={modalOpen}
        />
      )}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        TransitionComponent={Slide}
        message={<span>Link copied</span>}
        onClose={() => setOpen(false)}
      />
    </div>
  )
}

function AuthorCaption({ user, caption, createdAt }) {
  const classes = usePostStyles()

  return (
    <div style={{ display: 'flex' }}>
      <Avatar
        src={user.profile_image}
        alt="User avatar"
        style={{ marginRight: 14, width: 32, height: 32 }}
      />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Link to={`/${user.username}`}>
          <Typography
            variant="subtitle2"
            component="span"
            className={classes.username}
          >
            {user.username}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            className={classes.postCaption}
            style={{ paddingLeft: 0 }}
            dangerouslySetInnerHTML={{ __html: caption }}
          />
          <Typography
            style={{ marginTop: 16, marginBottom: 4, display: 'inline-block' }}
            color="textSecondary"
            variant="caption"
          >
            {formatDateToNowShort(createdAt)}
          </Typography>
        </Link>
      </div>
    </div>
  )
}

function UserComment({ comment }) {
  const classes = usePostStyles()
  return (
    <div style={{ display: 'flex' }}>
      <Avatar
        src={comment.user.profile_image}
        alt="User avatar"
        style={{ marginRight: 14, width: 32, height: 32 }}
      />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Link to={`/${comment.user.username}`}>
          <Typography
            variant="subtitle2"
            component="span"
            className={classes.username}
          >
            {comment.user.username}
          </Typography>
          <Typography
            variant="body2"
            component="span"
            className={classes.postCaption}
            style={{ paddingLeft: 0 }}
          >
            {comment.content}
          </Typography>
          <Typography
            style={{ marginTop: 16, marginBottom: 4, display: 'inline-block' }}
            color="textSecondary"
            variant="caption"
          >
            {formatDateToNowShort(comment.created_at)}
          </Typography>
        </Link>
      </div>
    </div>
  )
}

function LikeButton({ likes, authorId, postId }) {
  const classes = usePostStyles()
  const { currentUserId } = React.useContext(UserContext)
  const isAlreadyLiked = likes.some(({ user_id }) => user_id === currentUserId)
  const [liked, setLiked] = React.useState(isAlreadyLiked)
  const Icon = liked ? UnlikeIcon : LikeIcon
  const className = liked ? classes.liked : classes.like
  const onClick = liked ? handleUnlike : handleLike
  const variables = {
    postId,
    userId: currentUserId,
    profileId: authorId
  }
  const [likePost] = useMutation(LIKE_POST)
  const [unlikePost] = useMutation(UNLIKE_POST)

  function handleLike() {
    setLiked(true)
    likePost({ variables })
  }

  function handleUnlike() {
    setLiked(false)
    unlikePost({ variables })
  }

  return <Icon className={className} onClick={onClick} />
}

function SaveButton({ savedPosts, postId }) {
  const classes = usePostStyles()
  const { currentUserId } = React.useContext(UserContext)
  const isAlreadySaved = savedPosts.some(
    ({ user_id }) => user_id === currentUserId
  )
  const [saved, setSaved] = React.useState(isAlreadySaved)
  const Icon = saved ? RemoveIcon : SaveIcon
  const onClick = saved ? handleRemove : handleSave
  const [savePost] = useMutation(SAVE_POST)
  const [unsavePost] = useMutation(UNSAVE_POST)
  const variables = {
    postId,
    userId: currentUserId
  }

  function handleSave() {
    setSaved(true)
    savePost({ variables })
  }

  function handleRemove() {
    setSaved(false)
    unsavePost({ variables })
  }

  return <Icon className={classes.saveIcon} onClick={onClick} />
}

function Comment({ postId }) {
  const classes = usePostStyles()
  const { currentUserId } = React.useContext(UserContext)
  const [content, setContent] = React.useState('')
  const [createComment] = useMutation(CREATE_COMMENT)

  function handleAddComment() {
    const variables = {
      postId,
      userId: currentUserId,
      content
    }
    createComment({ variables })
    setContent('')
  }

  return (
    <div className={classes.commentContainer}>
      <TextField
        fullWidth
        value={content}
        placeholder="Add a comment..."
        multiline
        rowsMax={2}
        rows={1}
        onChange={event => setContent(event.target.value)}
        className={classes.textField}
        InputProps={{
          classes: {
            root: classes.root,
            underline: classes.underline
          }
        }}
      />
      <Button
        onClick={handleAddComment}
        color="primary"
        className={classes.commentButton}
        disabled={!content.trim()}
      >
        Post
      </Button>
    </div>
  )
}

export default Post
